import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  Link,
} from '@mui/joy';
import { X as CloseIcon, Question } from '@phosphor-icons/react';
import ReactMarkdown from 'react-markdown';
import { apiClient } from '../../lib/api';
import type { PageHelpArticle } from '../../hooks/usePageHelp';

interface KnowledgeBaseDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Article route to load (already resolved by the help button). */
  route: string | null;
}

/**
 * Right-anchored "How to use this page" panel. Loads the per-page Knowledge Base
 * article (summary + screenshot + markdown body) for the given route.
 */
export const KnowledgeBaseDrawer: React.FC<KnowledgeBaseDrawerProps> = ({ open, onClose, route }) => {
  const navigate = useNavigate();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['page-help', route],
    enabled: open && !!route,
    staleTime: 1000 * 60 * 30,
    queryFn: async () =>
      (await apiClient.get<PageHelpArticle>(
        `/api/support/page-help/?route=${encodeURIComponent(route as string)}`,
      )).data,
  });

  // The drawer header already says "How to use <title>", so drop the article's
  // leading H1 to avoid a duplicate heading. (The full markdown still feeds the AI.)
  const body = (data?.body_markdown || '').replace(/^#\s+.*\n+/, '');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ content: { sx: { width: { xs: '100%', sm: 460 }, display: 'flex', flexDirection: 'column' } } }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography level="title-lg" startDecorator={<Question weight="duotone" />}>
          {data?.title ? `How to use ${data.title}` : 'How to use this page'}
        </Typography>
        <IconButton variant="plain" onClick={onClose} aria-label="Close help">
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress aria-label="Loading page guide" />
          </Box>
        )}

        {!isLoading && data && (
          <>
            {data.summary && (
              <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
                {data.summary}
              </Typography>
            )}
            {data.screenshot_url && (
              <Box
                component="img"
                src={data.screenshot_url}
                alt={`${data.title} screenshot`}
                loading="lazy"
                sx={{ width: '100%', borderRadius: 'md', border: '1px solid', borderColor: 'divider', mb: 2 }}
              />
            )}
            <Box
              sx={{
                '& h1': { fontSize: '1.25rem', mt: 2, mb: 1 },
                '& h2': { fontSize: '1.05rem', mt: 2, mb: 0.5 },
                '& p': { my: 1, lineHeight: 1.6 },
                '& ul': { pl: 3, my: 1 },
                '& li': { my: 0.5 },
                '& code': { px: 0.5, borderRadius: 'sm', backgroundColor: 'background.level2' },
              }}
            >
              <ReactMarkdown>{body}</ReactMarkdown>
            </Box>
          </>
        )}

        {isError && <Alert color="danger" endDecorator={<Button size="sm" variant="soft" color="danger" onClick={() => refetch()}>Retry</Button>}>Could not load the page guide. Please try again.</Alert>}

        {!isLoading && !isError && !data && (
          <Typography level="body-sm">No help is available for this page yet.</Typography>
        )}
      </Box>

      <Divider />
      <Box sx={{ p: 2 }}>
        <Link
          component="button"
          level="body-sm"
          onClick={() => {
            onClose();
            navigate('/support');
          }}
        >
          Still need help? Ask the support assistant →
        </Link>
      </Box>
    </Drawer>
  );
};
